/**
 * scheduledApi.js — API functions for the Scheduled Reports page.
 *
 * fetchSchedules              — load all schedules for a campus
 * fetchScheduleFilterOptions  — load all filter option datasets
 * fetchFrequencies            — load frequency options (Daily, Weekly, Monthly)
 * editSchedule                — update a schedule
 * deleteSchedules             — delete one or more schedules
 * sendScheduleReport          — trigger immediate send of a schedule
 */

import { apiFetch, apiSave, apiTransaction } from '../../../../services/api.js';
import { getAuthHeaders } from '../../../../services/auth';

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => getAuthHeaders();

// ---------------------------------------------------------------------------
// Fetch Schedules
// ---------------------------------------------------------------------------

/**
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export async function fetchSchedules(campusId) {
    return apiFetch({
        procedure: 'getreportschedules',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

// ---------------------------------------------------------------------------
// Fetch Frequencies
// ---------------------------------------------------------------------------

/**
 * @returns {Promise<Array>} — [{ window_frequency_id, frequency_name }]
 */
export async function fetchFrequencies() {
    return apiFetch({
        table: 'att_window_frequency',
        procedure: 'fetchall',
        reqType: 'direct',
    });
}

// ---------------------------------------------------------------------------
// Fetch Filter Options (full set for all report types)
// ---------------------------------------------------------------------------

/**
 * @param {number} campusId
 * @returns {Promise<object>}
 */
export async function fetchScheduleFilterOptions(campusId) {
    try {
        const [portalFilters, eventTypes, reasonCodes, devices] = await Promise.all([
            fetchPortalFilters(campusId),
            apiFetch({
                table: 'att_event_types',
                procedure: 'fetchall',
                reqType: 'direct',
                pKey: 'campus_id',
                pVal: campusId,
            }),
            apiFetch({
                table: 'core_reason_codes',
                procedure: 'fetchbyparameter',
                reqType: 'direct',
                pKey: 'campus_id',
                pVal: campusId,
            }),
            apiFetch({
                procedure: 'sp_dev_getInovonicsDevices',
                reqType: 'stored',
                payload: {
                    device_filter: '',
                    device_category_filter: '1,2,3,6,7,8,9,10,11,12,15,16,17',
                    campus_id: campusId,
                },
            }),
        ]);

        const residentsWithName = (portalFilters.residents || []).map((r) => ({
            ...r,
            resident_name: `${r.resident_first_name || ''} ${r.resident_last_name || ''}`.trim() || '(unknown)',
        }));

        // att_event_types comes back in table order; sort it for the checkbox list.
        // Mirrors fetchFilterOptions in alertDetailsApi.js.
        const eventTypesSorted = [...(eventTypes || [])].sort((a, b) =>
            (a.event_type_name || '').localeCompare(b.event_type_name || '', undefined, { sensitivity: 'base' }),
        );

        return {
            areas: portalFilters.areas || [],
            areaGroups: portalFilters.areaGroups || [],
            areaTypes: portalFilters.areaTypes || [],
            residents: residentsWithName,
            residentGroups: portalFilters.residentGroups || [],
            users: portalFilters.users || [],
            userGroups: portalFilters.userGroups || [],
            deviceCategories: portalFilters.deviceCategories || [],
            devices: devices || [],
            eventTypes: eventTypesSorted,
            reasonCodes: reasonCodes || [],
        };
    } catch (err) {
        console.error('[scheduledApi] fetchScheduleFilterOptions error:', err);
        return {};
    }
}

async function fetchPortalFilters(campusId) {
    const body = {
        rep_type: 'getportalfilters',
        campus_id: campusId,
        device_category_filter: '1',
        area_type_filter: '1',
        user_filter: '1',
        resident_group_filter: '1',
        area_filter: '0',
        area_group_filter: '1',
        user_group_filter: '1',
        resident_filter: '1',
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(body),
        });
        const raw = await resp.json();
        const data = raw?.Res || raw;

        return {
            areas: data?.areas || [],
            areaGroups: data?.area_groups || [],
            areaTypes: data?.area_types || [],
            residents: data?.residents || [],
            residentGroups: data?.resident_groups || [],
            users: data?.users || [],
            userGroups: data?.user_groups || [],
            deviceCategories: data?.device_categories || [],
        };
    } catch (err) {
        console.error('[scheduledApi] fetchPortalFilters error:', err);
        return {};
    }
}

// ---------------------------------------------------------------------------
// Edit Schedule
// ---------------------------------------------------------------------------

/**
 * @param {object} payload — new schedule data
 * @param {object} oldPayload — original schedule data (for audit)
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export async function editSchedule(payload, oldPayload, session) {
    return apiTransaction({
        transType: 'schedule_edit',
        payload: [payload],
        oldPayload: oldPayload ? [oldPayload] : undefined,
        campusId: session.campusId,
        userId: session.userId,
        clientId: session.clientId,
    });
}

// ---------------------------------------------------------------------------
// Create Schedule (new)
// ---------------------------------------------------------------------------

/**
 * @param {object} payload — full schedule_add payload
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export async function createSchedule(payload, session) {
    return apiTransaction({
        transType: 'schedule_add',
        payload: [payload],
        campusId: session.campusId,
        userId: session.userId,
        clientId: session.clientId,
    });
}

// ---------------------------------------------------------------------------
// Delete Schedules
// ---------------------------------------------------------------------------

/**
 * @param {Array<{report_id: number}>} items
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export async function deleteSchedules(items, session) {
    const payload = items.map((item) => ({
        report_id: item.report_id,
        modified_by: session.userId,
    }));
    return apiSave({
        table: 'core_report_definition',
        payload,
        pKey: 'report_id',
        condition: 'primary',
        method: 'DELETE',
        campusId: session.campusId,
        clientId: session.clientId,
        userId: session.userId,
    });
}

// ---------------------------------------------------------------------------
// Send Schedule Report (immediate trigger)
// ---------------------------------------------------------------------------

/**
 * @param {object} schedule — the schedule row data
 * @param {number} campusId
 * @param {string} clientId
 * @returns {Promise<[boolean, string?]>}
 */
export async function sendScheduleReport(schedule, campusId, clientId) {
    const payload = {
        Content: [{
            campus_id: schedule.campus_id || campusId,
            description: schedule.description,
            source: 'portal',
            report_id: schedule.report_id,
            report_filter_terms: schedule.report_filter_terms,
            report_interval_id: schedule.report_interval_id,
            report_receiver: schedule.report_receiver,
            report_user_ids: schedule.report_user_ids || '',
            report_type_id: schedule.report_type_id,
            last_sent: schedule.last_sent,
            pdf_summary: schedule.pdf_summary,
            csv_details: schedule.csv_details,
            pdf_details: schedule.pdf_details,
        }],
        CampusId: campusId,
        ClientId: clientId,
    };

    try {
        // Fire-and-forget (nowait pattern from V1)
        fetch(getBaseUrl() + import.meta.env.VITE_SEND_REPORT, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(payload),
        });
        return [true];
    } catch (err) {
        console.error('[scheduledApi] sendScheduleReport error:', err);
        return [false, 'Failed to send report.'];
    }
}
